import { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { axiosInstance, fmtDT, getApiErrorMessage, Modal } from './_shared';
import EventForm, { EMPTY_EVENT } from './EventForm';
import AdminPhasesView from './AdminPhasesView';
import AdminRegistrationView from './AdminRegistrationView';
import EventAdminsModal from './EventAdminsModal';

export default function EventsTab({ isSuperAdmin, userId }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('list'); // list | create | edit | registrations
  const [editing, setEditing] = useState(null);
  const [regEvent, setRegEvent] = useState(null);
  const [deletingEvent, setDeletingEvent] = useState(null);
  const [adminsEvent, setAdminsEvent] = useState(null);   // co-admins modal
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await axiosInstance.get('/admin/events'); setEvents(r.data); }
    catch { toast.error('Failed to load events'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const createEvent = async form => {
    if (form === null) { setView('list'); load(); return; } // cricket events handle their own save
    try { await axiosInstance.post('/admin/events', form); toast.success('Event created!'); setView('list'); load(); }
    catch (e) { toast.error(getApiErrorMessage(e, 'Failed')); }
  };

  const deleteEvent = async (eventId) => {
    try {
      await axiosInstance.delete(`/admin/events/${eventId}`);
      toast.success('Event deleted');
      setDeletingEvent(null);
      load();
    } catch (e) { toast.error(getApiErrorMessage(e, 'Failed to delete event')); }
  };

  const updateEvent = async form => {
    if (form?._cricket) {
      try {
        const { _cricket, ...payload } = form;
        await axiosInstance.patch(`/v1/admin/events/${editing.id}/cricket`, payload);
        toast.success('Cricket event updated!');
        setView('list'); load();
      } catch (e) { toast.error(getApiErrorMessage(e, 'Failed to update')); }
      return;
    }
    try { await axiosInstance.put(`/admin/events/${editing.id}`, form); toast.success('Event updated!'); setView('list'); load(); }
    catch (e) { toast.error(getApiErrorMessage(e, 'Failed')); }
  };

  const toggleActive = async (id, current) => {
    try {
      await axiosInstance.patch(`/admin/events/${id}/active`);
      toast.success(current ? 'Event deactivated' : 'Event activated');
      load();
    } catch { toast.error('Failed'); }
  };

  const requestPublish = async (id) => {
    try {
      await axiosInstance.post(`/v1/admin/events/${id}/request-publish`);
      toast.success('Publish requested');
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (typeof detail === 'object' && detail.blocked_fields) {
        toast.error(`Blocked: ${detail.blocked_fields.map(f => f.field.replace('_value_id', '')).join(', ')} have pending values`);
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Request failed');
      }
    }
  };

  const publishEvent = async (id) => {
    try {
      await axiosInstance.post(`/v1/admin/events/${id}/publish`);
      toast.success('Event published!');
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (typeof detail === 'object' && detail.blocked_fields) {
        toast.error(`Blocked: ${detail.blocked_fields.map(f => f.field.replace('_value_id', '')).join(', ')} have pending values`);
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Publish failed');
      }
    }
  };

  const completeEvent = async (ev) => {
    if (!window.confirm(
      `Mark "${ev.title}" as completed?\n\nAuction, teams, fixtures and live scoring become read-only. ` +
      'Results stay editable. Only a Super Admin can reopen it.'
    )) return;
    try {
      await axiosInstance.post(`/v1/admin/events/${ev.id}/complete`, {});
      toast.success('Tournament marked completed');
      load();
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Could not complete the tournament'));
    }
  };

  const reopenEvent = async (ev) => {
    const reason = window.prompt(`Reopen "${ev.title}" for changes? Optionally enter a reason:`, '');
    if (reason === null) return;
    try {
      await axiosInstance.post(`/v1/admin/events/${ev.id}/reopen`, { reason: reason || null });
      toast.success('Tournament reopened');
      load();
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Could not reopen the tournament'));
    }
  };

  // Convert ISO UTC string to local datetime-local format
  const toFormDate = iso => {
    if (!iso) return '';
    const date = new Date(iso);
    // Format as YYYY-MM-DDTHH:MM in local timezone
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  if (view === 'create') return (
    <div>
      <button onClick={() => setView('list')} className="flex items-center gap-2 text-sm font-bold text-gray-500 mb-4">
        <i className="ph-bold ph-arrow-left" /> Back
      </button>
      <h2 className="text-lg font-extrabold mb-4">Create New Event</h2>
      <EventForm onSave={createEvent} onCancel={() => setView('list')} canPropose={true} />
    </div>
  );

  if (view === 'edit') {
    const isCricketEdit = !!editing?.cricket_config;
    const initialData = {
      ...editing,
      start_time: toFormDate(editing.start_time),
      end_time: toFormDate(editing.end_time),
      sub_type: editing.sub_type || (editing.cricket_config ? 'Cricket' : ''),
      cricket_config: editing.cricket_config ? {
        ...editing.cricket_config,
        registration_type: editing.cricket_config.registration_type || 'free',
        prize_pool_json: editing.cricket_config.prize_pool_json || { winner: '', runner_up: '', mvp: '', best_batter: '', best_bowler: '' },
        knockout_stages: editing.cricket_config.knockout_stages || [],
      } : { ...EMPTY_EVENT.cricket_config },
      organizer_name: editing.organizer_name || '',
      organizer_contact: editing.organizer_contact || '',
      organizer_email: editing.organizer_email || '',
      visibility: editing.visibility || 'public',
      competition_type_value_id: editing.competition_type_value_id || '',
      match_duration_estimate: editing.match_duration_estimate || '',
    };
    return (
      <div>
        <button onClick={() => setView('list')} className="flex items-center gap-2 text-sm font-bold text-gray-500 mb-4">
          <i className="ph-bold ph-arrow-left" /> Back
        </button>
        <h2 className="text-lg font-extrabold mb-4">Edit Event</h2>
        <EventForm
          initial={initialData}
          onSave={updateEvent}
          onCancel={() => setView('list')}
          isCricketEdit={isCricketEdit}
          canPropose={true}
        />
      </div>
    );
  }

  if (view === 'registrations' && regEvent) {
    return <AdminRegistrationView event={regEvent} onBack={() => { setView('list'); setRegEvent(null); load(); }} />;
  }

  if (view === 'phases' && editing) {
    return <AdminPhasesView event={editing} onBack={() => { setView('list'); setEditing(null); }} />;
  }

  const statusBadge = (ev) => {
    // For display: show computed status (Ongoing/Planned/Completed) for published events
    // The actual ev.event_status ('published') is used for logic (showing Registrations button)
    let displayStatus = ev.event_status;
    if (ev.event_status === 'published') {
      // A published tournament is Planned until it starts, then Ongoing until an
      // admin completes it (publishing results or "Mark completed") — never
      // "Completed" just because the end date passed.
      const start = ev.start_time ? new Date(ev.start_time) : null;
      displayStatus = start && new Date() < start ? 'Planned' : 'Ongoing';
    } else if (ev.event_status === 'completed') {
      displayStatus = 'Completed';
    }
    const map = {
      draft: 'bg-gray-100 text-gray-600',
      pending_publish: 'bg-yellow-100 text-yellow-700',
      published: 'bg-green-100 text-green-700',
      Planned: 'bg-blue-100 text-blue-700',
      Ongoing: 'bg-green-100 text-green-700',
      Completed: 'bg-gray-100 text-gray-500',
      cancelled: 'bg-red-100 text-red-600',
    };
    return (
      <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${map[displayStatus] || 'bg-gray-100 text-gray-500'}`}
        data-testid={`event-status-${ev.id}`}>
        {displayStatus?.replace(/_/g, ' ')}
      </span>
    );
  };

  return (
    <div className="space-y-4" data-testid="events-tab">
      {/* Delete Event Confirmation Modal */}
      {adminsEvent && (
        <EventAdminsModal event={adminsEvent} userId={userId}
          onClose={() => setAdminsEvent(null)} onChanged={load} />
      )}

      {deletingEvent && (
        <Modal title="Delete Event" onClose={() => setDeletingEvent(null)}>
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Are you sure you want to delete <strong>{deletingEvent.title}</strong>?
            </p>
            <p className="text-xs text-red-500">
              This will permanently delete the event and all associated registrations, bookings, and configurations.
            </p>
            <div className="flex gap-2">
              <button onClick={() => setDeletingEvent(null)} className="flex-1 py-3 bg-gray-200 text-gray-700 font-bold rounded-2xl">
                Cancel
              </button>
              <button onClick={() => deleteEvent(deletingEvent.id)} className="flex-1 py-3 bg-red-500 text-white font-extrabold rounded-2xl">
                Delete Event
              </button>
            </div>
          </div>
        </Modal>
      )}

      <button onClick={() => setView('create')}
        className="w-full py-3 bg-[#2B3A67] text-white font-extrabold rounded-2xl flex items-center justify-center gap-2"
        data-testid="create-event-btn">
        <i className="ph-bold ph-plus" /> Create New Event
      </button>

      {/* Search */}
      <div className="relative">
        <i className="ph-bold ph-magnifying-glass absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
        <input
          type="text"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search events by name or venue..."
          className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm bg-white"
          data-testid="events-search-input"
        />
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#E54562]" /></div>
      ) : events.length === 0 ? (
        <div className="text-center py-16 text-gray-400"><i className="ph-duotone ph-calendar text-5xl mb-3" /><p>No events</p></div>
      ) : (
        <div className="space-y-3">
          {(() => {
            const q = search.trim().toLowerCase();
            const filtered = q
              ? events.filter(ev =>
                  ev.title?.toLowerCase().includes(q) ||
                  ev.venue?.name?.toLowerCase().includes(q) ||
                  ev.venue?.city?.toLowerCase().includes(q)
                )
              : events;
            if (filtered.length === 0) return (
              <div className="text-center py-10 text-gray-400 text-sm">No events match "{search}"</div>
            );
            return filtered.map(ev => (
            <div key={ev.id} data-testid={`event-card-${ev.id}`}
              className={`bg-white rounded-2xl p-4 shadow-sm border transition-opacity ${ev.is_active === false ? 'border-red-100 opacity-60' : ev.cricket_config ? 'border-blue-100' : 'border-gray-100'}`}>
              <div className="flex items-start gap-3">
                {ev.image_url && <img src={ev.image_url} alt="" className="w-14 h-14 rounded-xl object-cover shrink-0" />}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-[#2B3A67]/10 text-[#2B3A67]">{ev.type}</span>
                    {ev.sub_type && <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">{ev.sub_type}</span>}
                    {statusBadge(ev)}
                    {ev.is_active === false && <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-red-100 text-red-600">Inactive</span>}
                    {ev.is_co_admin && <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-[#3AAFA9]/15 text-[#2a8a85]" data-testid={`co-admin-badge-${ev.id}`}>Co-admin</span>}
                    {ev.can_manage === false && <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-gray-100 text-gray-500" data-testid={`view-only-badge-${ev.id}`}>View only</span>}
                  </div>
                  <p className="font-extrabold mt-1 truncate">{ev.title}</p>
                  <p className="text-xs text-gray-400">{fmtDT(ev.start_time)} · {ev.venue?.name}</p>
                  {/* Cricket config badges */}
                  {ev.cricket_config && (
                    <div className="flex gap-1.5 mt-1.5 flex-wrap">
                      {ev.cricket_config.format_label && <span className="text-xs bg-blue-50 text-blue-600 px-1.5 py-0.5 rounded-full">{ev.cricket_config.format_label}</span>}
                      {ev.cricket_config.ball_type_label && <span className="text-xs bg-orange-50 text-orange-600 px-1.5 py-0.5 rounded-full">{ev.cricket_config.ball_type_label}</span>}
                      {ev.cricket_config.registration_mode_label && <span className="text-xs bg-purple-50 text-purple-600 px-1.5 py-0.5 rounded-full">{ev.cricket_config.registration_mode_label}</span>}
                      {ev.cricket_config.max_teams && <span className="text-xs bg-green-50 text-green-600 px-1.5 py-0.5 rounded-full">{ev.cricket_config.max_teams} teams</span>}
                      {ev.cricket_config.category_label && <span className="text-xs bg-yellow-50 text-yellow-700 px-1.5 py-0.5 rounded-full">{ev.cricket_config.category_label}</span>}
                    </div>
                  )}
                </div>
              </div>
              <div className="flex gap-2 mt-3">
                {ev.can_manage && (
                  <button onClick={() => { setEditing(ev); setView('edit'); }}
                    className="flex-1 py-2 rounded-xl border-2 border-[#2B3A67] text-[#2B3A67] font-bold text-xs"
                    data-testid={`edit-event-${ev.id}`}>
                    Edit
                  </button>
                )}
                {/* Cricket publish workflow buttons */}
                {ev.can_manage && ev.cricket_config && ev.event_status === 'draft' && (
                  <button onClick={() => requestPublish(ev.id)}
                    className="flex-1 py-2 rounded-xl bg-yellow-500 text-white font-bold text-xs"
                    data-testid={`request-publish-${ev.id}`}>
                    Request Publish
                  </button>
                )}
                {ev.cricket_config && (ev.event_status === 'draft' || ev.event_status === 'pending_publish') && isSuperAdmin && (
                  <button onClick={() => publishEvent(ev.id)}
                    className="flex-1 py-2 rounded-xl bg-green-600 text-white font-bold text-xs"
                    data-testid={`publish-event-${ev.id}`}>
                    Publish
                  </button>
                )}
                {/* Standard toggle for non-cricket or published events */}
                {ev.can_manage && !ev.cricket_config && (
                  <button onClick={() => toggleActive(ev.id, ev.is_active !== false)}
                    className={`flex-1 py-2 rounded-xl font-bold text-xs text-white ${ev.is_active === false ? 'bg-green-500' : 'bg-red-400'}`}>
                    {ev.is_active === false ? 'Activate' : 'Deactivate'}
                  </button>
                )}
                {/* Registrations for published / completed cricket events */}
                {ev.can_manage && ev.cricket_config && (ev.event_status === 'published' || ev.event_status === 'completed') && (
                  <button onClick={() => { setRegEvent(ev); setView('registrations'); }}
                    className="flex-1 py-2 rounded-xl bg-[#2B3A67] text-white font-bold text-xs"
                    data-testid={`view-regs-${ev.id}`}>
                    Registrations
                  </button>
                )}
                {/* Tournament completion: the event's admins (owner, co-admins, Super Admin) complete, Super Admin reopens */}
                {ev.can_manage && ev.cricket_config && ev.event_status === 'published' && (
                  <button onClick={() => completeEvent(ev)}
                    className="flex-1 py-2 rounded-xl border-2 border-gray-400 text-gray-600 font-bold text-xs"
                    data-testid={`complete-event-${ev.id}`}>
                    Mark Completed
                  </button>
                )}
                {ev.cricket_config && ev.event_status === 'completed' && isSuperAdmin && (
                  <button onClick={() => reopenEvent(ev)}
                    className="flex-1 py-2 rounded-xl border-2 border-orange-400 text-orange-600 font-bold text-xs"
                    data-testid={`reopen-event-${ev.id}`}>
                    Reopen
                  </button>
                )}
                {/* Manage Phases for cricket events */}
                {ev.can_manage && ev.cricket_config && (
                  <button onClick={() => { setEditing(ev); setView('phases'); }}
                    className="flex-1 py-2 rounded-xl bg-[#3AAFA9] text-white font-bold text-xs"
                    data-testid={`manage-phases-${ev.id}`}>
                    Phases
                  </button>
                )}
                {/* Owner + co-admins (any admin can see who to ask) */}
                <button onClick={() => setAdminsEvent(ev)}
                  className="py-2 px-3 rounded-xl border-2 border-gray-200 text-gray-600 font-bold text-xs"
                  title="Event admins" aria-label="Event admins"
                  data-testid={`event-admins-${ev.id}`}>
                  <i className="ph-bold ph-users-three" />
                </button>
                {/* Delete Event - Super Admin only */}
                {isSuperAdmin && (
                  <button onClick={() => setDeletingEvent(ev)}
                    className="py-2 px-3 rounded-xl bg-red-500 text-white font-bold text-xs"
                    data-testid={`delete-event-${ev.id}`}>
                    <i className="ph-bold ph-trash" />
                  </button>
                )}
              </div>
            </div>
          ));
          })()}
        </div>
      )}
    </div>
  );
}

// ─── Tab: Shop / Products ──────────────────────────────────────────────────────
const EMPTY_PRODUCT = {
  name: '', brand: '', description: '', base_price: 0, discounted_price: '',
  images: [''], category: 'Apparel',
  variants: [{ id: '', size: '', color: '', stock: 10 }],
};
